import { useEffect, useState } from "react";
import { Zap, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import ScorecardInfoTip from "./ScorecardInfoTip";
import MoMDeltaPill from "./MoMDeltaPill";

/**
 * Conversion Optimization Score — GA4-derived KPI (Yoquelvis · 25 %).
 *
 * Composite of Lead Conversion Rate (35 %) + Form Completion Rate (30 %)
 * + Engagement Rate proxy for Landing Effectiveness (20 %). The remaining
 * 15 % (CRO with Clarity) is intentionally left unmeasured until the
 * Clarity API integration lands — hence `conversion_score_max` = 85.
 *
 * Data source: `GET /api/scorecard/ga4/conversion` — cached 6h on the
 * backend so the pie doesn't hammer the Data API on every hover.
 */
const fmtNum = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));

export default function ConversionScoreCard({ dateFrom, dateTo, unit }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo)   params.date_to   = dateTo;
        if (unit)     params.unit      = unit;
        const r = await api.get("/scorecard/ga4/conversion", { params });
        if (mounted) setData(r.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || e.message);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [dateFrom, dateTo, unit]);

  if (loading && !data) {
    return (
      <section className="surface p-4" data-testid="ga4-conversion-loading">
        <div className="animate-pulse bg-slate-100" style={{ height: 200, borderRadius: 8 }}/>
      </section>
    );
  }
  if (error) {
    return (
      <section className="surface p-4" data-testid="ga4-conversion-error">
        <div className="text-xs font-bold text-red-600">Error GA4: {error}</div>
      </section>
    );
  }
  if (!data) return null;

  const score = data.conversion_optimization_score || 0;
  const max   = data.conversion_score_max || 100;
  const pct   = (score / max) * 100;
  const color = pct >= 70 ? "#16a34a" : pct >= 40 ? "#f97316" : "#dc2626";
  const k     = data.kpis || {};

  return (
    <section className="surface p-4" data-testid="ga4-conversion-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="kpi-icon-clean" style={{
          width: 32, height: 32, color: "#6366f1",
          boxShadow: "inset 0 0 0 1px #6366f133",
        }}>
          <Zap size={16}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-gray-500 flex items-center gap-1">
            Conversion Optimization Score
            <ScorecardInfoTip
              label="Conversion Optimization Score"
              formula="Composite ponderado GA4: Lead Conv 35% + Form Completion 30% + Engagement (proxy Landing) 20% + CRO Clarity 15% (pendiente)"
              extra={`Yoquelvis · Scorecard Jul 2026 · 25% del peso individual. Actualmente 85% de cobertura (falta Clarity API). Datos GA4 cacheados 6h. Fuente: ${data.properties_ok}/${data.properties_checked} properties.`}
              source={`GA4 Data API × ${data.properties_checked} properties · ${data.period.start} → ${data.period.end}`}
            />
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">
            {data.period.start} → {data.period.end} · {fmtNum(data.totals?.sessions)} sesiones
          </div>
        </div>
      </div>

      {/* Score headline */}
      <div className="flex items-baseline gap-2 mb-3"
        style={{ fontFamily: "Manrope, sans-serif" }}>
        <span className="font-extrabold tabular-nums"
          style={{ fontSize: 40, color, letterSpacing: "-0.03em", lineHeight: 1 }}>
          {score.toFixed(1)}
        </span>
        <span className="text-sm font-bold text-gray-500">/ {max}</span>
        <MoMDeltaPill metric="conversion" />
        <span className="ml-auto text-[10px] font-bold text-gray-400 tabular-nums">
          {data.properties_ok}/{data.properties_checked} sites
        </span>
      </div>

      {/* Sub-KPIs */}
      <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(3, 1fr)" }}>
        {[
          { label: "Lead Conv",  value: k.lead_conversion_rate, unit: "%", tint: "#0ea5e9" },
          { label: "Form Compl", value: k.form_completion_rate, unit: "%", tint: "#8b5cf6" },
          { label: "Engagement", value: k.engagement_rate,      unit: "%", tint: "#14b8a6" },
        ].map((c) => (
          <div key={c.label} style={{
            padding: "8px 10px", borderRadius: 6,
            background: `${c.tint}0d`, border: `1px solid ${c.tint}22`,
          }} data-testid={`ga4-subkpi-${c.label.replace(/ /g,'-').toLowerCase()}`}>
            <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{c.label}</div>
            <div className="tabular-nums font-extrabold"
              style={{ fontSize: 16, color: c.tint, letterSpacing: "-0.02em",
                       fontFamily: "Manrope, sans-serif" }}>
              {(c.value || 0).toFixed(2)}{c.unit}
            </div>
          </div>
        ))}
      </div>

      {/* Footer note when Clarity is pending or details when present */}
      {k.cro_clarity_pct === null ? (
        <div className="mt-2 text-[10px] text-gray-500 flex items-center gap-1">
          <ExternalLink size={10}/>
          CRO Clarity (15% del score) N/A para este scope
        </div>
      ) : data.clarity_details && !data.clarity_details.error && (
        <div className="mt-3 pt-3" style={{ borderTop: "1px solid #f1f5f9" }}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
              Clarity CRO · últimos 3 días
            </div>
            <span className="text-[10px] font-bold tabular-nums text-gray-600">
              {fmtNum(data.clarity_details.sessions)} sesiones
            </span>
          </div>
          <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
            {[
              { label: "Dead Clicks",   value: data.clarity_details.dead_click_rate,      danger: 10 },
              { label: "Quick Backs",   value: data.clarity_details.quick_back_rate,      danger: 5 },
              { label: "Rage Clicks",   value: data.clarity_details.rage_click_rate,      danger: 2 },
              { label: "Script Errors", value: data.clarity_details.script_error_rate,    danger: 1 },
            ].map((m) => {
              const c = m.value >= m.danger ? "#dc2626" : m.value >= m.danger/2 ? "#f97316" : "#16a34a";
              return (
                <div key={m.label} className="flex items-center gap-1 text-[10px]">
                  <span className="text-gray-600 font-semibold flex-1 truncate">{m.label}</span>
                  <span className="tabular-nums font-extrabold" style={{ color: c }}>
                    {m.value.toFixed(2)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
